import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';
import { messageService, eventOptionsService } from '../services/api';
import MessagesSidebar from '../components/MessagesSidebar';

const NewMessage = () => {
    const navigate = useNavigate();
    const { language } = useLanguage();
    const isRtl = language === 'fa';
    const t = (fa, en) => (isRtl ? fa : en);

    const [eventOptions, setEventOptions] = useState([]);
    const [eventsLoading, setEventsLoading] = useState(true);

    const [eventSlug, setEventSlug] = useState('');
    const [title, setTitle] = useState('');
    const [text, setText] = useState('');
    const [attachment, setAttachment] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [errors, setErrors] = useState({});

    useEffect(() => {
        const fetchEventOptions = async () => {
            setEventsLoading(true);
            try {
                const data = await eventOptionsService.getSubmittedEventSlugs();
                setEventOptions(data || []);
            } catch (err) {
                console.error('Event options fetch error:', err);
                setEventOptions([]);
            } finally {
                setEventsLoading(false);
            }
        };
        fetchEventOptions();
    }, []);

    const handleFileChange = (e) => {
        const selected = e.target.files?.[0];
        if (selected) setAttachment(selected);
    };

    const validate = () => {
        const next = {};
        if (!title.trim()) next.title = t('عنوان پیام الزامی است', 'Title is required');
        if (!text.trim()) next.text = t('متن پیام الزامی است', 'Message body is required');
        setErrors(next);
        return Object.keys(next).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validate()) return;

        setSubmitting(true);
        try {
            await messageService.createMessage({
                event: eventSlug || undefined,
                title,
                text,
                attachment
            });
            navigate('/messages');
        } catch (err) {
            console.error('Send message error:', err);
            setErrors({ submit: t('خطا در ارسال پیام. دوباره تلاش کنید.', 'Failed to send message. Please try again.') });
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="max-w-[1136px] mx-auto px-3 sm:px-4 py-4 lg:py-8 font-iransansRegular overflow-hidden" dir={isRtl ? 'rtl' : 'ltr'}>
            <div className="flex flex-col lg:flex-row gap-4 lg:gap-6">
                <MessagesSidebar
                    activeKey="messages"
                    onNavigate={(key) => {
                        if (key === 'messages') navigate('/messages');
                        if (key === 'finance') navigate('/finance');
                        if (key === 'account') navigate('/account');
                        if (key === 'support') navigate('/support');
                        if (key === 'logout') navigate('/logout');
                    }}
                />

                <div className="flex-1 w-full min-w-0">
                    <motion.h1
                        className="text-xl sm:text-2xl lg:text-[32px] font-extrabold text-[#292DD8] mb-6 lg:mb-8"
                        initial={{ opacity: 0, y: -20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                    >
                        {t('ارسال پیام جدید', 'Send New Message')}
                    </motion.h1>

                    <motion.form
                        onSubmit={handleSubmit}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: 0.1 }}
                        className="space-y-5 sm:space-y-6"
                    >
                        {/* Event dropdown - optional */}
                        <div>
                            <label className="block text-sm sm:text-base font-bold text-[#204286] mb-2">
                                {t('رویداد', 'Event')}
                                <span className="text-gray-400 font-normal text-xs sm:text-sm mr-1.5">
                                    {t('(اختیاری)', '(optional)')}
                                </span>
                            </label>
                            <div className="relative">
                                <select
                                    value={eventSlug}
                                    onChange={(e) => setEventSlug(e.target.value)}
                                    disabled={eventsLoading}
                                    className="w-full appearance-none bg-[#EBF1FB] border border-[#D6E1F5] rounded-xl px-4 py-3 text-sm sm:text-base text-[#204286] focus:outline-none focus:ring-2 focus:ring-[#292DD8]/40 transition-all disabled:opacity-60"
                                >
                                    <option value="">
                                        {eventsLoading
                                            ? t('در حال بارگذاری...', 'Loading...')
                                            : t('یک رویداد را انتخاب نمایید', 'Select an event')}
                                    </option>
                                    {eventOptions.map((opt) => (
                                        <option key={opt.slug} value={opt.slug}>
                                            {opt.title || opt.slug}
                                        </option>
                                    ))}
                                </select>
                                <svg
                                    className={`w-4 h-4 absolute ${isRtl ? 'left-4' : 'right-4'} top-1/2 -translate-y-1/2 text-[#292DD8] pointer-events-none`}
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                </svg>
                            </div>
                        </div>

                        {/* Title */}
                        <div>
                            <label className="block text-sm sm:text-base font-bold text-[#204286] mb-2">
                                {t('عنوان پیام', 'Message Title')}
                            </label>
                            <input
                                type="text"
                                value={title}
                                onChange={(e) => setTitle(e.target.value)}
                                placeholder={t('عنوان پیام را وارد کنید', 'Enter a title')}
                                className={`w-full bg-[#EBF1FB] border ${
                                    errors.title ? 'border-red-400' : 'border-[#D6E1F5]'
                                } rounded-xl px-4 py-3 text-sm sm:text-base text-[#204286] placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[#292DD8]/40 transition-all`}
                            />
                            {errors.title && <p className="text-red-500 text-xs mt-1.5">{errors.title}</p>}
                        </div>

                        {/* Text */}
                        <div>
                            <label className="block text-sm sm:text-base font-bold text-[#204286] mb-2">
                                {t('متن پیام', 'Message Body')}
                            </label>
                            <textarea
                                value={text}
                                onChange={(e) => setText(e.target.value)}
                                placeholder={t('متن پیام خود را بنویسید', 'Type your message')}
                                rows={6}
                                className={`w-full bg-[#EBF1FB] border ${
                                    errors.text ? 'border-red-400' : 'border-[#D6E1F5]'
                                } rounded-xl px-4 py-3 text-sm sm:text-base text-[#204286] placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[#292DD8]/40 transition-all resize-none`}
                            />
                            {errors.text && <p className="text-red-500 text-xs mt-1.5">{errors.text}</p>}
                        </div>

                        {/* File upload - optional */}
                        <div>
                            <label className="block text-sm sm:text-base font-bold text-[#204286] mb-2">
                                {t('آپلود عکس', 'Upload Image')}
                                <span className="text-gray-400 font-normal text-xs sm:text-sm mr-1.5">
                                    {t('(اختیاری)', '(optional)')}
                                </span>
                            </label>
                            <label
                                htmlFor="file-upload"
                                className="flex items-center justify-between w-full bg-[#EBF1FB] border border-[#D6E1F5] rounded-xl px-4 py-3 text-sm sm:text-base text-gray-400 cursor-pointer hover:bg-[#E2EBFA] transition-all"
                            >
                                <span className={attachment ? 'text-[#204286] font-medium truncate' : ''}>
                                    {attachment ? attachment.name : t('برای اضافه کردن عکس کلیک نمایید', 'Click to add an image')}
                                </span>
                                <svg className="w-5 h-5 text-[#292DD8] shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                </svg>
                            </label>
                            <input id="file-upload" type="file" accept="image/*" onChange={handleFileChange} className="hidden" />
                        </div>

                        {errors.submit && <p className="text-red-500 text-sm">{errors.submit}</p>}

                        <motion.button
                            type="submit"
                            disabled={submitting}
                            whileHover={{ scale: submitting ? 1 : 1.02 }}
                            whileTap={{ scale: submitting ? 1 : 0.98 }}
                            className="px-8 py-2.5 rounded-lg text-white font-bold text-sm sm:text-base shadow-lg bg-gradient-to-r from-[#292DD8] to-[#4B4FEE] hover:from-[#1f23a8] hover:to-[#3a3ecc] transition-all duration-300 disabled:opacity-60 disabled:cursor-not-allowed"
                        >
                            {submitting ? t('در حال ارسال...', 'Sending...') : t('ارسال', 'Send')}
                        </motion.button>
                    </motion.form>
                </div>
            </div>
        </div>
    );
};

export default NewMessage;